import { StringUtils } from "shared-ts-utils-core";
import { ChimneySensorStatusQueryInput } from "./ChimneySensorStatusQueryInput";
import {
  BatteryLevelEnum,
  ChimneySensorCableStatusEnum,
  ChimneySensorStatusEnum,
  isSensorSerialNumber,
  MunicipalityId,
  parseStreetAddressExprOrUndef,
  PostalCodeId,
  SensorId,
} from "firefly-shared-ts-domain";
import {
  andExpr,
  containsExpr,
  equalsExpr,
  Expr,
  fieldExpr,
  Query,
  QueryDto,
  SortItemDto,
  valExpr,
} from "shared-ts-utils-query";

export class ChimneySensorStatusQuery extends Query {
  readonly municipality?: MunicipalityId;
  readonly postalCode?: PostalCodeId;
  readonly id?: SensorId;
  readonly search?: string;
  readonly status?: ChimneySensorStatusEnum;
  readonly retired?: boolean;
  readonly cableStatus?: ChimneySensorCableStatusEnum;
  readonly batteryLevel?: BatteryLevelEnum;
  readonly sort?: Array<SortItemDto>;

  constructor(args: ChimneySensorStatusQueryInput) {
    super({
      sort: args.sort,
      where: ChimneySensorStatusQuery.resolveWhere(args),
    });
    this.municipality = StringUtils.notEmptyOrUndef(args.municipality);
    this.postalCode = StringUtils.notEmptyOrUndef(args.zipCode);
    this.id = StringUtils.notEmptyOrUndef(args.id);
    this.search = StringUtils.notEmptyOrUndef(args.search);
    this.status = args.status ?? undefined;
    this.retired = args.retired ?? undefined;
    this.cableStatus = args.cableStatus ?? undefined;
    this.batteryLevel = args.batteryLevel ?? undefined;
    this.sort = args.sort ?? undefined;
  }

  static resolveWhere(args: ChimneySensorStatusQueryInput): Expr | undefined {
    let where: Expr | undefined = undefined;
    if (StringUtils.isNotBlank(args.id)) {
      const expr = containsExpr(fieldExpr("id"), valExpr(args.id));
      where = where != null ? andExpr(where, expr) : expr;
    }

    if (StringUtils.isNotBlank(args.municipality)) {
      const expr = equalsExpr(
        fieldExpr("municipalityId"),
        valExpr(args.municipality)
      );
      where = where != null ? andExpr(where, expr) : expr;
    }

    if (StringUtils.isNotBlank(args.zipCode)) {
      const expr = equalsExpr(
        fieldExpr("postalAddress.code"),
        valExpr(args.zipCode)
      );
      where = where != null ? andExpr(where, expr) : expr;
    }

    if (StringUtils.isNotBlank(args.status)) {
      const expr = equalsExpr(fieldExpr("status"), valExpr(args.status));
      where = where != null ? andExpr(where, expr) : expr;
    }

    if (StringUtils.isNotBlank(args.cableStatus)) {
      const expr = equalsExpr(
        fieldExpr("cableStatus"),
        valExpr(args.cableStatus)
      );
      where = where != null ? andExpr(where, expr) : expr;
    }

    if (StringUtils.isNotBlank(args.batteryLevel)) {
      const expr = equalsExpr(
        fieldExpr("batteryLevel"),
        valExpr(args.batteryLevel)
      );
      where = where != null ? andExpr(where, expr) : expr;
    }

    const expr = equalsExpr(
      fieldExpr("retired"),
      valExpr(args.retired === true)
    );
    where = where != null ? andExpr(where, expr) : expr;

    if (args.search != null && StringUtils.isNotBlank(args.search)) {
      if (isSensorSerialNumber(args.search)) {
        const expr = equalsExpr(fieldExpr("id"), valExpr(args.search));
        where = where != null ? andExpr(where, expr) : expr;
      } else {
        const expr = parseStreetAddressExprOrUndef(args.search);
        if (expr != null) {
          where = where != null ? andExpr(where, expr) : expr;
        }
      }
    }
    return where;
  }
}

export interface ChimneySensorStatusQueryDto extends QueryDto {
  municipality?: MunicipalityId;
  postalCode?: PostalCodeId;
  id?: string;
  search?: string;
  status?: ChimneySensorStatusEnum;
  cableStatus?: ChimneySensorCableStatusEnum;
  batteryLevel?: BatteryLevelEnum;
  retired?: boolean;
  sort?: Array<SortItemDto>;
}
